import { fetchApi } from "~/lib/api";
import type {
  YotoApplyIconRequest,
  YotoAuthConnectUrl,
  YotoCard,
  YotoDisplayIcon,
  YotoIntegrationStatus,
  YotoSyncResult,
} from "~/types";

export const customerYotoService = {
  /**
   * Get the current Yoto integration status for the authenticated user
   */
  async getStatus() {
    return fetchApi<YotoIntegrationStatus>("/customer/yoto/status");
  },

  /**
   * Get the Yoto OAuth authorization URL
   */
  async getConnectUrl() {
    return fetchApi<YotoAuthConnectUrl>("/customer/yoto/connect");
  },

  /**
   * Exchange OAuth code for tokens and create the initial Yoto playlist
   */
  async callback(code: string) {
    return fetchApi<{ success: boolean; playlistId: string }>(
      "/customer/yoto/callback",
      {
        method: "POST",
        body: JSON.stringify({ code }),
      },
    );
  },

  /**
   * Fetch the user's Yoto cards/playlists
   */
  async getCards() {
    return fetchApi<{ cards: YotoCard[] }>("/customer/yoto/cards");
  },

  /**
   * Fetch full content details for a single Yoto card by cardId
   */
  async getCard(cardId: string) {
    return fetchApi<{ card: YotoCard }>(
      `/customer/yoto/cards/${encodeURIComponent(cardId)}`,
    );
  },

  /**
   * Fetch the user's custom Yoto display icons
   */
  async getDisplayIcons() {
    return fetchApi<{ icons: YotoDisplayIcon[] }>(
      "/customer/yoto/display-icons",
    );
  },

  /**
   * Manually sync all user's songs to Yoto
   */
  async sync() {
    return fetchApi<YotoSyncResult>("/customer/yoto/sync", {
      method: "POST",
    });
  },

  /**
   * Disconnect the Yoto integration
   */
  async disconnect() {
    return fetchApi<{ success: boolean; message: string }>(
      "/customer/yoto/disconnect",
      {
        method: "DELETE",
      },
    );
  },

  /**
   * Update the list of song order IDs excluded from Yoto sync.
   * Also triggers a sync automatically.
   */
  async updateExcludedSongs(excludedSongIds: number[]) {
    return fetchApi<{ success: boolean; syncResult: YotoSyncResult }>(
      "/customer/yoto/excluded-songs",
      {
        method: "PATCH",
        body: JSON.stringify({ excludedSongIds }),
      },
    );
  },

  /**
   * Set a custom track icon for a song using an existing Yoto display icon.
   * Triggers a sync after updating.
   */
  async setTrackIcon({ orderId, displayIconId }: YotoApplyIconRequest) {
    return fetchApi<{ success: boolean; syncResult: YotoSyncResult }>(
      `/customer/yoto/songs/${orderId}/track-icon`,
      {
        method: "POST",
        body: JSON.stringify({ displayIconId }),
      },
    );
  },

  /**
   * Generate new pixel art for a Yoto track icon and apply it.
   * Allowed only once per order. Triggers a sync after completion.
   */
  async generateArtwork(orderId: number, prompt: string) {
    return fetchApi<{
      success: boolean;
      iconId: string;
      iconUrl: string;
      syncResult: YotoSyncResult;
    }>(`/customer/yoto/songs/${orderId}/generate-artwork`, {
      method: "POST",
      body: JSON.stringify({ prompt }),
    });
  },
};
